import { KEY } from '@constants/key';
import { followSession } from '@lib/customization/unsavedWork';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { isServerError } from '@store/error';
import { showSnackbar } from '@store/reducers/snackbarSlice';
import { removeAuth } from '../reducers/authSlice';

const baseQuery = fetchBaseQuery({
  baseUrl: import.meta.env.PROD ? '/api' : `${window.location.protocol}//${window.location.hostname}:3000/api`,
  prepareHeaders: (headers) => {
    const token = localStorage.getItem(KEY.ACCESS_TOKEN);
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  },
});

const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);

  if (import.meta.env.DEV) {
    console.info('api.request:', `-${result.meta?.request.method}`, result.meta?.request.url);
  }

  if (result.error && result.error.status === 401) {
    try {
      const refreshToken = localStorage.getItem(KEY.REFRESH_TOKEN);

      if (!refreshToken) throw new Error('No refresh token available');

      const refreshResult = await baseQuery({
        url: '/v1/auth/refresh-token',
        method: 'POST',
        body: { refreshToken },
      }, api, extraOptions);

      if (refreshResult.data) {
        type DataType = { accessToken: string, refreshToken: string };
        localStorage.setItem(KEY.ACCESS_TOKEN, (refreshResult.data as DataType).accessToken);

        result = await baseQuery(args, api, extraOptions);
      } else {
        if (isServerError(result)) {
          api.dispatch(showSnackbar({ severity: 'error', message: result.error.data.message }));
        }

        throw new Error('Refresh failed');
      }
    } catch (_) {
      localStorage.removeItem(KEY.ACCESS_TOKEN);
      localStorage.removeItem(KEY.REFRESH_TOKEN);

      // A session that ran out signs the user out without a reload: nobody is asked about
      // what they left unsaved in an editor until they are signed in again.
      followSession(undefined);
      api.dispatch(removeAuth());
    }
  }

  return result;
};

export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'Project', 'Inspection', 'Checklist', 'Folder', 'User', 'ApiKey', 'Config', 'Statistic',
    'Team', 'TeamShare', 'PoiType', 'AppSettings', 'ReportDesign', 'ReportBranding',
  ],
  endpoints: () => ({}),
});
